import {
  signInWithRedirect,
  SignInWithRedirectInput,
  signOut,
  fetchAuthSession,
} from 'aws-amplify/auth'
import { Hub } from 'aws-amplify/utils'
import { User } from 'models'
import { useEffect, useState, useRef } from 'react'
import { To, useLocation, useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import { clearCurrentUser, setCurrentUser } from 'store/AuthSlice'
import { setRedirectUrl, clearRedirectUrl } from 'store/RedirectSlice'
import { generatePath } from 'react-router-dom'
import { useCurrentStore } from './StoreHooks'
import { useLazyGetProfileQuery } from 'apis/UserApi'
import { TypeRoute } from 'core/TypeRoute'
import { useToast } from 'contexts'
import { useTranslation } from 'react-i18next'
import { clearTableAndOrderData } from 'store/OrderingSlice'

export const getAuthToken = async (): Promise<string | undefined> => {
  const session = await fetchAuthSession()

  return session.tokens?.idToken?.toString()
}

export const useCurrentUser = (): {
  isSignedIn: boolean
  currentUser: User | undefined
  isLoading: boolean
} => {
  const dispatch = useDispatch()
  const [getProfile] = useLazyGetProfileQuery()
  const { showToast } = useToast()
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const loadingInProgress = useRef<boolean>(false)
  const { store } = useCurrentStore()

  const currentUser = useSelector<RootState>(
    (state) => state.auth.currentUser,
  ) as User

  useEffect(() => {
    const loadInitialProfile = async () => {
      const isSignedIn = Boolean(await getAuthToken())

      if (isSignedIn && !currentUser) {
        await loadUserProfile()
      }
    }

    loadInitialProfile()

    const unsubscribe = Hub.listen('auth', async ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          await loadUserProfile()
          break
        case 'signedOut':
          dispatch(clearCurrentUser())
          break
        case 'signInWithRedirect_failure':
          // TODO: We will need to handle this corner case by showing on UI a popup or message about failure
          console.error('An error has occurred during the OAuth flow.')
          break
      }
    })

    return unsubscribe
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserProfile = async () => {
    if (loadingInProgress.current) return

    try {
      loadingInProgress.current = true
      setIsLoading(true)

      const profileResult = await getProfile({
        storeid: store?.id,
      }).unwrap()
      dispatch(setCurrentUser(profileResult))
    } catch (profileError) {
      console.error('Failed to fetch user profile:', profileError)
      showToast(
        t('Failed to load user profile. Please try again later.'),
        'error',
      )
    } finally {
      setIsLoading(false)
      loadingInProgress.current = false
    }
  }

  return {
    isSignedIn: !!currentUser,
    currentUser,
    isLoading,
  }
}

export const useNavigateToSignIn = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  return (to: To) => {
    dispatch(setRedirectUrl(location.pathname))
    navigate(to)
  }
}

export const useSignIn = () => {
  const location = useLocation()
  const dispatch = useDispatch()
  const redirectUrl = useSelector<RootState>(
    (state) => state.redirect.redirectUrl,
  ) as string | null

  return async (provider: SignInWithRedirectInput['provider']) => {
    // Set default redirect URL so it can automatically pick up the current page for redirection
    if (!redirectUrl) {
      dispatch(setRedirectUrl(location.pathname))
    }

    await signInWithRedirect({ provider })
  }
}

export const useSignOut = () => {
  const dispatch = useDispatch()
  const { store } = useCurrentStore()

  return async () => {
    // Clear all user-related data from Redux store
    dispatch(clearCurrentUser())
    dispatch(clearTableAndOrderData())

    // Sign out from AWS Cognito
    await signOut()

    // Setup redirect URL
    let redirectUrl: string = TypeRoute.Home

    if (store?.table_info) {
      redirectUrl = generatePath(TypeRoute.TableOrdering, {
        storeId: store?.id,
        tableId: store.table_info.table_id,
      })
    }

    if (store) {
      redirectUrl = generatePath(TypeRoute.StoreInfo, { storeId: store.id })
    }

    dispatch(setRedirectUrl(redirectUrl))
  }
}

export const useRedirectPrevPage = () => {
  const navigate = useNavigate()
  const redirectToHomePage = useRedirectToHomePage()
  const dispatch = useDispatch()
  const redirectUrl = useSelector<RootState>(
    (state) => state.redirect.redirectUrl,
  ) as string | null

  const redirect = () => {
    if (redirectUrl) {
      dispatch(clearRedirectUrl())

      if (redirectUrl === TypeRoute.SignIn) {
        return redirectToHomePage()
      }

      return navigate(redirectUrl)
    }

    return redirectToHomePage()
  }

  return redirect
}

export const useRedirectToHomePage = () => {
  const navigate = useNavigate()
  const { store } = useCurrentStore()

  return () => {
    if (store) {
      navigate(generatePath(TypeRoute.StoreInfo, { storeId: store.id }))
    } else {
      navigate(TypeRoute.Home)
    }
  }
}
